/**
 * useLiveSync — keeps this client in sync with changes made elsewhere (other
 * tabs, devices, or Claude via MCP). Opens a WebSocket to /live; on each "change"
 * event it invalidates the note/folder/search queries so the grid refetches.
 *
 * The connection is server-authenticated by the session cookie. We reconnect
 * with capped backoff and re-sync on reconnect to catch anything missed while
 * offline. The open note editor keeps its own draft state and doesn't re-seed
 * from the query, so a background refetch never clobbers in-progress typing.
 */

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CLIENT_ID } from "./clientId";

const HEARTBEAT_MS = 30_000;
const PONG_TIMEOUT_MS = 10_000;
const MAX_BACKOFF_MS = 30_000;
const RESYNC_DEBOUNCE_MS = 250;

export function useLiveSync(enabled: boolean): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || typeof WebSocket === "undefined") return;

    let ws: WebSocket | null = null;
    let heartbeat: ReturnType<typeof setInterval> | undefined;
    let pongTimer: ReturnType<typeof setTimeout> | undefined;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let resyncTimer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    let closed = false;

    // Debounced so a burst of events (or a reconnect storm) coalesces into a
    // single refetch instead of one per message.
    const resync = () => {
      clearTimeout(resyncTimer);
      resyncTimer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["notes"] });
        queryClient.invalidateQueries({ queryKey: ["folders"] });
        queryClient.invalidateQueries({ queryKey: ["search"] });
      }, RESYNC_DEBOUNCE_MS);
    };

    const connect = () => {
      if (closed) return;
      const proto = location.protocol === "https:" ? "wss" : "ws";
      // ?cid lets the server skip echoing our own writes back to this tab.
      ws = new WebSocket(`${proto}://${location.host}/live?cid=${CLIENT_ID}`);

      ws.onopen = () => {
        attempt = 0;
        // Catch up on anything that changed while we were disconnected.
        resync();
        heartbeat = setInterval(() => {
          if (ws?.readyState !== WebSocket.OPEN) return;
          ws.send("ping");
          // Expect ANY message back within the window; a half-open socket (sleep/
          // wake, captive portal) never fires onclose, so without this watchdog
          // the client would silently stop receiving updates.
          clearTimeout(pongTimer);
          pongTimer = setTimeout(() => {
            try {
              ws?.close();
            } catch {
              /* noop */
            }
          }, PONG_TIMEOUT_MS);
        }, HEARTBEAT_MS);
      };

      ws.onmessage = (e) => {
        clearTimeout(pongTimer); // any message proves the socket is alive
        if (e.data === "pong") return;
        resync();
      };

      const scheduleReconnect = () => {
        clearInterval(heartbeat);
        clearTimeout(pongTimer);
        if (closed) return;
        const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt) * (0.5 + Math.random());
        attempt++;
        reconnectTimer = setTimeout(connect, delay);
      };

      ws.onclose = scheduleReconnect;
      ws.onerror = () => ws?.close();
    };

    // On returning to the app (tab visible / network back), reconnect a dead
    // socket and resync immediately rather than waiting for the next heartbeat.
    const onResume = () => {
      if (document.visibilityState === "hidden") return;
      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        clearTimeout(reconnectTimer);
        attempt = 0;
        connect();
      } else if (ws.readyState === WebSocket.OPEN) {
        resync();
      }
    };
    document.addEventListener("visibilitychange", onResume);
    window.addEventListener("online", onResume);

    connect();

    return () => {
      closed = true;
      clearInterval(heartbeat);
      clearTimeout(pongTimer);
      clearTimeout(reconnectTimer);
      clearTimeout(resyncTimer);
      document.removeEventListener("visibilitychange", onResume);
      window.removeEventListener("online", onResume);
      ws?.close();
    };
  }, [enabled, queryClient]);
}
